import { Checkbox } from "@octopusdeploy/design-system-components";
import { Permission, type Repository } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import type { ActionEvent } from "~/analytics/Analytics";
import { Action, AnalyticView, useAnalyticTrackedActionDispatch } from "~/analytics/Analytics";
import Markdown from "~/components/Markdown";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { OverflowMenuConverterVNext } from "~/components/OverflowMenu/OverflowMenuConverterVNext";
import { PageContent } from "~/components/PageContent/PageContent";
import { ExpandableFormSection, MarkdownEditor, required, Summary, Text } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import NameSummaryWithSlug from "~/primitiveComponents/form/Slugs/NameSummaryWithSlug";
import SlugEditor from "~/primitiveComponents/form/Slugs/SlugEditor";
import { links } from "../../../../../../../packages/portal-routes/src/routes";
import { DeleteParentEnvironmentDialogLayout } from "./DeleteParentEnvironmentDialogLayout";
export async function parentEnvironmentPageLoader(repository: Repository, spaceId: string, environmentId: string): Promise<LoaderData> {
    const parentEnvironment = repository.ParentEnvironments.get({ Id: environmentId, SpaceId: spaceId });
    return {
        parentEnvironment: await parentEnvironment,
    };
}
interface LoaderData {
    parentEnvironment: ParentEnvironmentModel;
}
interface ParentEnvironmentPageProps {
    loaderData: LoaderData;
}
export interface ParentEnvironmentModel {
    SpaceId: string;
    Id: string;
    Name: string;
    Slug: string;
    Description?: string;
    SortOrder: number;
    UseGuidedFailure: boolean;
}
export function ParentEnvironmentPage({ loaderData }: ParentEnvironmentPageProps) {
    const trackAction = useAnalyticTrackedActionDispatch();
    const { navigate } = useSpaceAwareNavigation();
    const { model, setModel, formProps, createSaveAction, getFieldError } = useForm({
        formName: "Parent Environment",
        initialModel: loaderData.parentEnvironment,
        onSubmit: handleSave,
    });
    async function handleSave(repository: Repository, model: ParentEnvironmentModel): Promise<ParentEnvironmentModel> {
        const ev: ActionEvent = {
            action: Action.Save,
            resource: "Parent Environment",
        };
        return await trackAction("Save Parent Environment", ev, async () => {
            return await repository.ParentEnvironments.modify(model);
        });
    }
    const descriptionSummary = () => {
        return model.Description ? Summary.summary(<Markdown markup={model.Description}/>) : Summary.placeholder("No description provided");
    };
    const handleDeleteConfirm = async () => {
        navigate(links.infrastructureEnvironmentsPage.generateUrl({ spaceId: model.SpaceId }));
    };
    const overflowMenu = OverflowMenuConverterVNext.convertAll([
        OverflowMenuItems.dialogItem("Delete", <DeleteParentEnvironmentDialogLayout environment={model} onDelete={handleDeleteConfirm}/>, {
            permission: Permission.EnvironmentDelete,
            environment: "*",
        }),
    ]);
    return (<>
            <AnalyticView resource="ParentEnvironment"/>

            <PageContent header={{
            title: model.Name,
            primaryAction: createSaveAction(),
            breadcrumbs: [{ label: "Parent Environments", pageUrl: links.infrastructureEnvironmentsPage.generateUrl({ spaceId: model.SpaceId }) }],
            overflowActions: overflowMenu.menuItems,
        }}>
                {overflowMenu.dialogs}
                <Form {...formProps}>
                    <ExpandableFormSection errorKey="name" title="Name" focusOnExpandAll summary={model.Name ? Summary.summary(<NameSummaryWithSlug name={model.Name} slug={model.Slug}/>) : Summary.placeholder("Please enter a name for your environment")} help="A short, memorable, unique name for this parent environment. Example: QA Testing.">
                        <Text value={model.Name} onChange={(Name) => setModel({ ...model, Name })} label="Name" validate={required("Please enter a environment name")} autoFocus={true}/>
                        <SlugEditor value={model.Slug} name={model.Name} originalSlug={loaderData.parentEnvironment.Slug ?? ""} onChange={(Slug) => setModel({ ...model, Slug })} label="Slug" validate={required("Please enter an environment slug")} error={getFieldError("Slug")}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="description" title="Description" summary={descriptionSummary()} help="Enter a description for your environment.">
                        <MarkdownEditor value={model.Description} label="Environment description" onChange={(Description) => setModel({ ...model, Description })}/>
                    </ExpandableFormSection>

                    <ExpandableFormSection errorKey="UseGuidedFailure" title="Default Guided Failure Mode" summary={model.UseGuidedFailure ? Summary.summary("Yes") : Summary.default("No")} help="Select whether guided failure mode is enabled by default">
                        <div>
                            <Checkbox value={model.UseGuidedFailure ?? false} label="Use guided failure mode by default" onChange={(UseGuidedFailure: boolean) => setModel({ ...model, UseGuidedFailure })} note={<span>If guided failure is enabled for an environment, Octopus Deploy will prompt for user intervention if a deployment fails in the environment.</span>}/>
                        </div>
                    </ExpandableFormSection>
                </Form>
            </PageContent>
        </>);
}
